import api from "../api/axios";

const read = (source, ...keys) => {
  if (!source || typeof source !== "object") return undefined;

  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key];
    }
  }

  const entries = Object.entries(source);
  for (const key of keys) {
    const match = entries.find(([entryKey]) => entryKey.toLowerCase() === key.toLowerCase());
    if (match && match[1] !== undefined && match[1] !== null) {
      return match[1];
    }
  }

  return undefined;
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableNumber = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeTextEnum = (value, fallback) => {
  if (typeof value !== "string" || !value.trim()) return fallback;
  return value.trim().replace(/[\s-]+/g, "_").toUpperCase();
};

const normalizeAction = (item) => {
  const rawAction = read(item, "primaryAction", "action");
  const actionUrl = read(item, "primaryActionUrl", "actionUrl");
  const actionLabel = read(item, "primaryActionLabel", "actionLabel");

  if (rawAction && typeof rawAction === "object") {
    return {
      type: normalizeTextEnum(read(rawAction, "type", "code", "action"), "VIEW_DETAILS"),
      label: read(rawAction, "label", "title") || actionLabel || null,
      url: read(rawAction, "url", "href", "targetUrl") || actionUrl || null,
    };
  }

  if (typeof rawAction === "string" && rawAction.startsWith("/")) {
    return { type: "VIEW_DETAILS", label: actionLabel || null, url: rawAction };
  }

  return {
    type: normalizeTextEnum(rawAction, "VIEW_DETAILS"),
    label: actionLabel || null,
    url: actionUrl || null,
  };
};

const normalizeItem = (item, index) => {
  const sourceType = normalizeTextEnum(read(item, "sourceType", "type"), "UNKNOWN");
  const sourceId = read(item, "sourceId", "entityId", "recordId");
  const relatedEntityId = read(item, "relatedEntityId", "relationId");
  const dueDate = read(item, "dueDate", "date");

  return {
    id: read(item, "id", "workItemId") || `${sourceType}:${sourceId ?? index}`,
    sourceType,
    sourceId,
    category: normalizeTextEnum(read(item, "category"), "OTHER"),
    direction: normalizeTextEnum(read(item, "direction"), "OPERATIONAL"),
    title: read(item, "title", "name") || "İsimsiz iş",
    description: read(item, "description", "detail") || "",
    relatedEntityId,
    relatedEntityName: read(item, "relatedEntityName", "relatedName") || "",
    dueDate: dueDate || null,
    daysRemaining: toNullableNumber(read(item, "daysRemaining", "remainingDays")),
    amount: toNullableNumber(read(item, "amount", "remainingAmount")),
    currencyCode: read(item, "currencyCode", "currency") || "TRY",
    status: read(item, "status") || "",
    urgency: normalizeTextEnum(read(item, "urgency", "priority"), "PLANNED"),
    branchId: read(item, "branchId"),
    branchName: read(item, "branchName") || "",
    detailUrl: read(item, "detailUrl", "url", "href") || null,
    primaryAction: normalizeAction(item),
  };
};

const normalizeCurrencyAmounts = (value) => {
  if (!Array.isArray(value)) return [];

  return value.map((entry) => ({
    currencyCode: String(read(entry, "currencyCode", "currency") || "TRY").trim().toUpperCase(),
    incomingAmount: toNumber(read(entry, "incomingAmount")),
    outgoingAmount: toNumber(read(entry, "outgoingAmount")),
  })).filter((entry) => entry.incomingAmount > 0 || entry.outgoingAmount > 0);
};

const readCurrencyAmounts = (summary, listKey, legacyIncomingKey, legacyOutgoingKey) => {
  const normalized = normalizeCurrencyAmounts(read(summary, listKey));
  if (normalized.length > 0) return normalized;

  const incomingAmount = toNumber(read(summary, legacyIncomingKey));
  const outgoingAmount = toNumber(read(summary, legacyOutgoingKey));
  return incomingAmount > 0 || outgoingAmount > 0
    ? [{ currencyCode: "TRY", incomingAmount, outgoingAmount }]
    : [];
};

const normalizeSummary = (summary = {}) => ({
  amountAggregation: read(summary, "amountAggregation") || "BY_CURRENCY",
  overdueCount: toNumber(read(summary, "overdueCount")),
  overdueAmountsByCurrency: readCurrencyAmounts(summary, "overdueAmountsByCurrency", "overdueIncomingAmount", "overdueOutgoingAmount"),
  todayCount: toNumber(read(summary, "todayCount")),
  todayAmountsByCurrency: readCurrencyAmounts(summary, "todayAmountsByCurrency", "todayIncomingAmount", "todayOutgoingAmount"),
  sevenDayCount: toNumber(read(summary, "sevenDayCount")),
  sevenDayAmountsByCurrency: readCurrencyAmounts(summary, "sevenDayAmountsByCurrency", "sevenDayIncomingAmount", "sevenDayOutgoingAmount"),
  thirtyDayCount: toNumber(read(summary, "thirtyDayCount")),
  thirtyDayAmountsByCurrency: readCurrencyAmounts(summary, "thirtyDayAmountsByCurrency", "thirtyDayIncomingAmount", "thirtyDayOutgoingAmount"),
  criticalStockCount: toNumber(read(summary, "criticalStockCount")),
  problemDocumentCount: toNumber(read(summary, "problemDocumentCount")),
});

export const normalizeWorkTrackingResponse = (response) => {
  const responseBody = response?.data ?? response ?? {};
  const payload = read(responseBody, "data", "result") ?? responseBody;
  const rawItems = read(payload, "items", "records", "list") ?? [];
  const items = Array.isArray(rawItems) ? rawItems.map(normalizeItem) : [];
  const pageSize = Math.max(1, toNumber(read(payload, "pageSize", "limit"), 20));
  const totalCount = Math.max(items.length, toNumber(read(payload, "totalCount", "count"), items.length));

  return {
    success: read(responseBody, "success", "isSuccess") !== false,
    message: read(responseBody, "message", "errorMessage") || "",
    summary: normalizeSummary(read(payload, "summary", "totals") || {}),
    items,
    totalCount,
    page: Math.max(1, toNumber(read(payload, "page", "currentPage"), 1)),
    pageSize,
    totalPages: Math.max(1, toNumber(read(payload, "totalPages"), Math.ceil(totalCount / pageSize) || 1)),
  };
};

const cleanParams = (params) => Object.fromEntries(
  Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== null && value !== "")
);

const WorkTrackingService = {
  getAll: async (params = {}, options = {}) => {
    const response = await api.get("/WorkTracking", {
      params: cleanParams(params),
      signal: options.signal,
    });
    return normalizeWorkTrackingResponse(response);
  },
};

export default WorkTrackingService;
